// Hook customizado para gerenciar autenticação
import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { authService } from "../services/authService";
import { storageService } from "../services/storageService";
import { validateLoginForm } from "../utils/validation";
import { MESSAGES } from "../utils/constants";
import { toast } from "react-toastify";
import logger from "../../../utils/logger";

/**
 * Hook para gerenciar autenticação e login
 * @returns {Object} Funções e estado de autenticação
 */
export const useAuth = () => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  /**
   * Realiza o processo de login (online ou offline)
   * @param {string} user - Nome de usuário
   * @param {string} password - Senha
   * @returns {Promise<Object>} Resultado do login {success, errors?, error?}
   */
  const login = useCallback(async (user, password) => {
    const trimmedUser = user.trim();
    const trimmedPassword = password.trim();

    logger.info("Iniciando processo de login", {
      user: trimmedUser,
      isOnline: navigator.onLine
    });

    // Validação dos campos
    const validation = validateLoginForm(trimmedUser, trimmedPassword);
    if (!validation.isValid) {
      logger.warn("Validação de campos falhou", validation.errors);
      return {
        success: false,
        errors: validation.errors
      };
    }

    setLoading(true);

    try {
      // Verificar se é cliente_sipremo (modo mock)
      const isMockUser = trimmedUser === "cliente_sipremo";

      if (isMockUser) {
        logger.info("Usuário mock detectado: cliente_sipremo");

        // Ativar modo mock
        storageService.setMockMode(true);

        // Realizar login mock
        const mockToken = await authService.loginMock(trimmedUser, trimmedPassword);

        localStorage.setItem(
          "pendingAuth",
          JSON.stringify({
            token: mockToken,
            user: trimmedUser,
            password: trimmedPassword,
            name: trimmedUser,
            email: "",
            enterprise: "ACM",
            type: "mock",
            isMock: true,
          })
        );
        storageService.setToken(mockToken);

        logger.debug("Redirecionando para /select-region (modo mock)");
        navigate("/select-region");

        return { success: true };
      }

      // Modo online
      if (navigator.onLine) {
        logger.info("Modo online detectado");

        // Desativar modo mock se não for cliente_sipremo
        storageService.setMockMode(false);

        try {
          const authData = await authService.loginOnline(trimmedUser, trimmedPassword);

          if (authData?.token) {
            localStorage.setItem(
              "pendingAuth",
              JSON.stringify({
                token: authData.token,
                user: trimmedUser,
                password: trimmedPassword,
                name: authData.name || trimmedUser,
                email: authData.email || "",
                enterprise: authData.enterprise || "ACM",
                type: authData.type || "",
                isMock: false,
              })
            );
            storageService.setToken(authData.token);
            localStorage.setItem("enterprise", authData.enterprise || "ACM");

            logger.debug("Redirecionando para /select-region");
            navigate("/select-region");
            
            return { success: true };
          } else {
            // Usuário não encontrado
            toast.error(MESSAGES.USER_NOT_FOUND);
            return { 
              success: false, 
              error: MESSAGES.USER_NOT_FOUND 
            };
          }
        } catch (error) {
          // Erro na requisição
          logger.error("Erro na requisição de login", error);
          toast.error(MESSAGES.USER_NOT_FOUND);
          return { 
            success: false, 
            error: MESSAGES.USER_NOT_FOUND 
          };
        }
      } 
      // Modo offline
      else {
        logger.info("Modo offline detectado");
        
        // Desativar modo mock se não for cliente_sipremo
        storageService.setMockMode(false);
        
        const isValid = authService.loginOffline(trimmedUser, trimmedPassword);

        if (isValid) {
          localStorage.setItem(
            "pendingAuth",
            JSON.stringify({
              token: null,
              user: trimmedUser,
              password: trimmedPassword,
              name: trimmedUser,
              email: "",
              enterprise: localStorage.getItem("enterprise") || "ACM",
              type: "offline",
              isMock: false,
            })
          );

          logger.debug("Redirecionando para /select-region (offline)");
          navigate("/select-region");
          
          return { success: true };
        } else {
          // Credenciais offline inválidas
          logger.warn("Credenciais offline inválidas");
          toast.error(MESSAGES.USER_NOT_FOUND);
          return { 
            success: false, 
            error: MESSAGES.USER_NOT_FOUND 
          };
        }
      }
    } catch (error) {
      logger.error("Erro inesperado no login", error);
      toast.error(MESSAGES.NETWORK_ERROR);
      return { 
        success: false, 
        error: MESSAGES.NETWORK_ERROR 
      };
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  return { 
    login, 
    loading 
  };
};

